/**
 * Joining bonuses on the Payments tab: what she can still win (with her
 * progress toward 10 visits), and what she has earned and whether it is paid.
 *
 * Renders from the `bonuses` field of GET /rn/payments. That field is new: an
 * older API sends none, and a nurse the rules never pay gets null. In both
 * cases, and when there is simply nothing to show, this renders nothing, so the
 * screen looks exactly as it did before the feature.
 */
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

import type { RnBonusEarned, RnBonusOffer, RnBonuses } from "@/lib/api";
import { colors, fonts, radius, spacing, typography } from "@/lib/theme";

function dollars(cents: number): string {
    return `$${(cents / 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
}

/** "Sep 30" from a New York `YYYY-MM-DD` key, with no timezone shift. */
export function formatDateKey(key: string): string {
    const d = new Date(`${key}T12:00:00Z`);
    if (Number.isNaN(d.getTime())) {
        return key;
    }
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

function daysLeftLabel(daysLeft: number): string {
    if (daysLeft <= 0) {
        return "Last day";
    }
    return daysLeft === 1 ? "1 day left" : `${daysLeft} days left`;
}

const EARNED_STATUS: Record<
    RnBonusEarned["status"],
    { label: string; bg: string; fg: string; icon: keyof typeof MaterialCommunityIcons.glyphMap }
> = {
    paid: { label: "Paid", bg: colors.success50, fg: colors.success600, icon: "check-circle" },
    on_the_way: {
        label: "On the way",
        bg: colors.teal50,
        fg: colors.teal600,
        icon: "clock-outline"
    },
    needs_bank: {
        label: "Needs bank account",
        bg: colors.error50,
        fg: colors.error600,
        icon: "bank-outline"
    }
};

function OfferRow({ offer }: { offer: RnBonusOffer }) {
    const isVisits = offer.bonusType === "ten_visits";
    const done = offer.progress ? Math.min(offer.progress.done, offer.progress.required) : 0;
    const required = offer.progress?.required ?? 1;
    return (
        <View style={styles.offerCard}>
            <View style={styles.offerTop}>
                <View style={styles.offerIcon}>
                    <MaterialCommunityIcons
                        name={isVisits ? "trophy-outline" : "gift-outline"}
                        size={20}
                        color={colors.teal600}
                    />
                </View>
                <View style={styles.offerTextCol}>
                    <Text style={styles.offerTitle}>
                        {isVisits
                            ? `Complete ${required} visits, get ${dollars(offer.amountCents)}`
                            : `Finish onboarding, get ${dollars(offer.amountCents)}`}
                    </Text>
                    <Text style={styles.offerSub}>
                        By {formatDateKey(offer.deadline)} · {daysLeftLabel(offer.daysLeft)}
                    </Text>
                </View>
            </View>
            {isVisits && offer.progress ? (
                <View style={styles.progressBlock}>
                    <View style={styles.progressTrack}>
                        <View
                            style={[
                                styles.progressFill,
                                { width: `${Math.round((done / required) * 100)}%` }
                            ]}
                        />
                    </View>
                    <Text style={styles.progressText}>
                        {done} of {required} visits done
                    </Text>
                </View>
            ) : null}
        </View>
    );
}

function EarnedRow({ item }: { item: RnBonusEarned }) {
    const s = EARNED_STATUS[item.status];
    return (
        <View style={styles.earnedRow}>
            <View style={styles.earnedTextCol}>
                <Text style={styles.earnedTitle}>{item.title}</Text>
                <Text style={styles.earnedSub}>
                    {item.status === "paid" && item.paidOn
                        ? `Earned ${formatDateKey(item.earnedOn)} · paid ${formatDateKey(item.paidOn)}`
                        : item.status === "needs_bank"
                          ? "Add a bank account to receive it. Message us in chat."
                          : `Earned ${formatDateKey(item.earnedOn)} · sent with your next payment`}
                </Text>
            </View>
            <View style={styles.earnedRight}>
                <Text style={styles.earnedAmount}>{dollars(item.amountCents)}</Text>
                <View style={[styles.pill, { backgroundColor: s.bg }]}>
                    <MaterialCommunityIcons name={s.icon} size={12} color={s.fg} />
                    <Text style={[styles.pillText, { color: s.fg }]}>{s.label}</Text>
                </View>
            </View>
        </View>
    );
}

export function BonusesCard({ bonuses }: { bonuses: RnBonuses | null | undefined }) {
    if (!bonuses || (bonuses.offers.length === 0 && bonuses.earned.length === 0)) {
        return null;
    }
    return (
        <View style={styles.section}>
            <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>Bonuses</Text>
            </View>
            {bonuses.offers.map((o) => (
                <OfferRow key={o.bonusType} offer={o} />
            ))}
            {bonuses.earned.length > 0 ? (
                <View style={styles.earnedCard}>
                    {bonuses.earned.map((e, i) => (
                        <View key={e.id}>
                            {i > 0 ? <View style={styles.divider} /> : null}
                            <EarnedRow item={e} />
                        </View>
                    ))}
                </View>
            ) : null}
        </View>
    );
}

const styles = StyleSheet.create({
    section: { paddingHorizontal: spacing.base, marginBottom: spacing.xl },
    sectionHeader: { marginBottom: spacing.sm },
    sectionTitle: { ...typography.subtitle, color: colors.textPrimary },

    offerCard: {
        backgroundColor: colors.surface,
        borderRadius: radius.lg,
        borderWidth: 1,
        borderColor: colors.teal100,
        padding: spacing.base,
        marginBottom: spacing.sm
    },
    offerTop: { flexDirection: "row", alignItems: "center", gap: spacing.md },
    offerIcon: {
        width: 38,
        height: 38,
        borderRadius: radius.full,
        backgroundColor: colors.teal50,
        alignItems: "center",
        justifyContent: "center"
    },
    offerTextCol: { flex: 1, gap: 2 },
    offerTitle: { fontSize: 14, fontFamily: fonts.semiBold, color: colors.textPrimary },
    offerSub: { fontSize: 12, fontFamily: fonts.medium, color: colors.textTertiary },
    progressBlock: { marginTop: spacing.md, gap: spacing.xs },
    progressTrack: {
        height: 8,
        borderRadius: radius.full,
        backgroundColor: colors.teal50,
        overflow: "hidden"
    },
    progressFill: { height: 8, borderRadius: radius.full, backgroundColor: colors.teal400 },
    progressText: { fontSize: 12, fontFamily: fonts.semiBold, color: colors.teal600 },

    earnedCard: {
        backgroundColor: colors.surface,
        borderRadius: radius.lg,
        borderWidth: 1,
        borderColor: colors.border,
        paddingHorizontal: spacing.base
    },
    earnedRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.md,
        paddingVertical: spacing.md
    },
    earnedTextCol: { flex: 1, gap: 2 },
    earnedTitle: { fontSize: 14, fontFamily: fonts.semiBold, color: colors.textPrimary },
    earnedSub: { fontSize: 12, fontFamily: fonts.medium, color: colors.textTertiary },
    earnedRight: { alignItems: "flex-end", gap: 4 },
    earnedAmount: { fontSize: 16, fontFamily: fonts.bold, color: colors.textPrimary },
    pill: {
        flexDirection: "row",
        alignItems: "center",
        gap: 3,
        borderRadius: radius.full,
        paddingHorizontal: 7,
        paddingVertical: 2
    },
    pillText: { fontSize: 11, fontFamily: fonts.semiBold },
    divider: { height: 1, backgroundColor: colors.border }
});
